import axios from "axios";
import { clearSessionKeys } from "../helpers/sessionStorageKeys";
import { SocketManager } from "../context/Socket/SocketContext";
import {
	isLicenseError,
	licenseReasonFrom,
	setLicenseNotice,
} from "../helpers/licenseNotice";

const api = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	withCredentials: true,
	timeout: 30000,
});

// Rotas de autenticação: um 401 aqui é resposta esperada (senha errada, sessão
// já encerrada) e quem chamou trata. Não devem disparar renovação nem logout.
const AUTH_ROUTES = ["/auth/login", "/auth/refresh_token", "/auth/me", "/auth/clear-session"];

const isAuthRoute = (url) => AUTH_ROUTES.some((route) => (url || "").includes(route));

// Encerramento de sessão vindo do servidor. Sem isto, um token substituído por um
// login mais recente devolvia erro em toda chamada e o usuário ficava numa tela
// vazia, sem nenhuma explicação.
let sessionEndedHandled = false;

const handleSessionEnded = () => {
	if (sessionEndedHandled || window.location.pathname === "/login") {
		return;
	}
	sessionEndedHandled = true;
	clearSessionKeys();
	localStorage.setItem("sessionExpired", "true");
	window.location.href = "/login";
};

// Licença Empsis inválida (403 ERR_LICENSE_INVALID): a empresa perdeu o acesso
// com a sessão aberta. Encerra a sessão e leva o motivo para a tela de login.
// Sem token no localStorage o boot do useAuth limpa o cookie e não reautentica,
// então não há volta automática para dentro do sistema.
let licenseBlockedHandled = false;

const handleLicenseBlocked = (reason) => {
	if (licenseBlockedHandled || window.location.pathname === "/login") {
		return;
	}
	licenseBlockedHandled = true;
	clearSessionKeys();
	setLicenseNotice(reason);
	window.location.href = "/login";
};

// Cliente sem interceptors: usado só para renovar o token, para que a renovação
// não caia nela mesma. `withCredentials` é obrigatório — no modo de timeout
// normal o access token do header já está vencido e quem salva a renovação é o
// cookie jrt.
const refreshClient = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	withCredentials: true,
	timeout: 30000,
});

// Uma renovação por vez: quando o token vence, várias requisições falham juntas
// e todas devem esperar o MESMO refresh, em vez de disparar um cada.
let refreshPromise = null;

export const refreshAccessToken = () => {
	if (refreshPromise) {
		return refreshPromise;
	}

	refreshPromise = (async () => {
		try {
			let stored = null;
			try {
				stored = JSON.parse(localStorage.getItem("token"));
			} catch (e) {
				stored = null;
			}

			const { data } = await refreshClient.post(
				"/auth/refresh_token",
				{},
				stored ? { headers: { Authorization: `Bearer ${stored}` } } : {}
			);

			if (!data || !data.token) {
				return null;
			}

			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			// O socket guarda o token do handshake: sem reaplicar, ele seguiria
			// tentando reconectar com a credencial vencida.
			SocketManager.applyToken(data.token);
			return data.token;
		} catch (e) {
			return null;
		} finally {
			refreshPromise = null;
		}
	})();

	return refreshPromise;
};

// O backend responde 403 "Invalid token..." quando o timeout normal está ativo e
// 401 ERR_SESSION_EXPIRED quando a sessão foi encerrada. Os dois merecem uma
// tentativa de renovação — a própria mensagem do 403 promete isso ("We'll try to
// assign a new one on next request"), promessa que até agora ninguém cumpria: o
// resultado era 403 em loop, sem renovar e sem deslogar.
const isTokenError = (status, data) => {
	const code = data && data.error;
	if (status === 401) {
		return code === "ERR_SESSION_EXPIRED";
	}
	if (status === 403) {
		return typeof code === "string" && code.startsWith("Invalid token");
	}
	return false;
};

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const status = error?.response?.status;
		const config = error?.config;

		if (status === 403 && isLicenseError(error) && !isAuthRoute(config?.url)) {
			handleLicenseBlocked(licenseReasonFrom(error));
			return Promise.reject(error);
		}

		// Empresa bloqueada por vencimento (402 ERR_COMPANY_BLOCKED):
		// redireciona para o Financeiro, onde o pagamento continua liberado
		if (status === 402 && window.location.pathname !== "/financeiro") {
			window.location.href = "/financeiro";
			return Promise.reject(error);
		}

		if (
			config &&
			!config.__isRetry &&
			!isAuthRoute(config.url) &&
			localStorage.getItem("token") &&
			isTokenError(status, error?.response?.data)
		) {
			config.__isRetry = true;

			const newToken = await refreshAccessToken();

			if (newToken) {
				// Repete a requisição original com a credencial nova. O interceptor
				// de request relê o token do localStorage, então basta reenviar.
				return api(config);
			}

			// Renovação recusada: a sessão acabou mesmo (vencida há muito tempo ou
			// substituída por outro login).
			handleSessionEnded();
		}

		return Promise.reject(error);
	}
);

export const openApi = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL,
	timeout: 30000,
});

export default api;
